import { Box, Divider, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChevronLeft } from 'lucide-react';
import { Link } from '@mui/material';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    replanningEditingStructuresState,
    replanningStageState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import ContouringContent from '../ContouringStep';
import StepHeader from '../StepHeader';
import ReplanningStageNavigator from './ReplanningStageNavigator';
import ReplanningStageTasks from './ReplanningStageTasks';
import { replanningStage } from './replanningStages';
import { useReplanningProgress } from './useReplanningProgress';
import { useReplanningStructureSets } from './useReplanningStructureSets';
import { useDeformationDisplayScope } from '~/hooks/viewer/useDeformationDisplayScope';
import { useDimOtherStructures } from './useDimOtherStructures';
import { useLockReplanningOrientation } from './useLockReplanningOrientation';
import { useMergeContouredRois } from './useMergeContouredRois';
import { useRegistrationQaMeasurement } from './useRegistrationQaMeasurement';
import { useStagePresentation } from './useStagePresentation';
import ReplanningControlBar from './ReplanningControlBar';
import { useReplanningStageStatus } from './useReplanningStageStatus';

export default function AdaptiveReplanningContent() {
    const intl = useIntl();
    const [stageId, setStageId] = useRecoilState(replanningStageState);
    const stage = replanningStage(stageId);
    const StageContent = stage.content;
    const progress = useReplanningProgress();
    const isEditingStructures = useRecoilValue(
        replanningEditingStructuresState
    );
    const setEditingStructures = useSetRecoilState(
        replanningEditingStructuresState
    );
    const { propagated } = useReplanningStructureSets();
    const status = useReplanningStageStatus(stage.id);
    const currentProgress = progress.find(
        (candidate) => candidate.stageId === stage.id
    );

    useStagePresentation(stage.presentation, !!propagated);
    useLockReplanningOrientation(stage.id);
    // Editing is a contouring surface: every structure stays at full strength.
    useDimOtherStructures(!isEditingStructures && stage.dimsOtherStructures);
    useDeformationDisplayScope(stage.id === 'register');
    useMergeContouredRois();
    useRegistrationQaMeasurement();

    if (isEditingStructures) {
        return (
            <Box height="100%" display="grid" gridTemplateRows="auto 1fr">
                <Box px={1.5} pt={1} pb={0.5}>
                    <Link
                        component="button"
                        type="button"
                        underline="always"
                        variant="caption"
                        onClick={() => setEditingStructures(false)}
                        data-testid="replanning-close-structure-editing"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                        }}
                    >
                        <ChevronLeft size={12} aria-hidden />
                        <FormattedMessage id="replanning.back" />
                    </Link>
                </Box>
                {/* Unwrapped: an auto-height wrapper would collapse its definite-height layout. */}
                {/* No image header: editing is pinned to the deformed target
                    and its propagated structures, with no blend or scan swap. */}
                <ContouringContent
                    titleId={null}
                    showAssistant={false}
                    showImages={false}
                />
            </Box>
        );
    }

    return (
        <Box
            height="100%"
            display="grid"
            gridTemplateRows="auto auto 1fr auto"
            overflow="hidden"
        >
            <Box>
                <StepHeader titleId="workflow.adapt" />
                <Divider sx={{ mt: 1 }} />
            </Box>
            <Box>
                <ReplanningStageNavigator
                    currentStageId={stage.id}
                    stageStates={status.stages}
                />
                <Typography
                    fontSize={11}
                    color="text.secondary"
                    px={1.5}
                    pt={0.5}
                    pb={1}
                >
                    {intl.formatMessage({ id: stage.descriptionId })}
                </Typography>
                <ReplanningStageTasks stage={currentProgress} />
                <Divider />
            </Box>
            <Box
                overflow="auto"
                minHeight={0}
                role="region"
                aria-label={intl.formatMessage({ id: stage.labelId })}
            >
                <StageContent />
            </Box>
            <ReplanningControlBar
                currentStageId={stage.id}
                status={status}
                onGoToStage={setStageId}
            />
        </Box>
    );
}
